import React, { useEffect, useMemo, useRef, useState } from 'react'
import { PageTitle, useLayout } from '../../../_metronic/layout/core'
import { axiosClientAuth } from '../../utils/axiosClient';
import { Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { KTIcon } from '../../../_metronic/helpers';
import Swal from 'sweetalert2';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useIntl } from 'react-intl';

/* eslint-disable react-hooks/exhaustive-deps */

type UserStatus = {
  active: boolean
}

type AccountUser = {
  id: string | number
  uuid?: string
  name: string;
  email?: string
  phone?: string
  status?: {
    self?: UserStatus
    ceind?: UserStatus
    celao?: UserStatus
    indai?: UserStatus
    indbp?: UserStatus
    indjp?: UserStatus
    indsp?: UserStatus
    indtt?: UserStatus
    indvi?: UserStatus
  }
  created_at?: string
  type?: string
  srno?: number;
}

type HopStatus = {
  active: boolean | null
}

type HopEntry = {
  id: string
  source?: { id: string | number; name: string; type: string }
  destination?: { id: string | number; name: string; type: string }
  status?: {
    self?: HopStatus
    ceind?: HopStatus
    celao?: HopStatus
    destination?: HopStatus
  }
  created_at?: string
  updated_at?: string
}

const parseJsonPreservingLargeIds = <T,>(raw: string): T => {
  return JSON.parse(raw.replace(/(:|\[|,)\s*(\d{16,})(?=\s*[,}\]])/g, '$1"$2"'))
}

const formatJsonId = (id: string | number) => {
  const value = String(id)
  return /^\d+$/.test(value) ? value : JSON.stringify(value)
}

export default function HopsManagement() {
  const intl = useIntl()
  const [data, setData] = useState<HopEntry[]>([]);
  const [vendorData, setVendorData] = useState<AccountUser[]>([])
  const [vendorSearched, setVendorSearched] = useState(false)
  const [vendorLoading, setVendorLoading] = useState(false)
  const [vendorSearchTerm, setVendorSearchTerm] = useState('')

  const [operatorData, setOperatorData] = useState<AccountUser[]>([])
  const [selectedOperator, setSelectedOperator] = useState<AccountUser[]>([])
  const [operatorLoading, setOperatorLoading] = useState(false)
  const [hopsPage, setHopsPage] = useState(1)
  const [hopsPageSize, setHopsPageSize] = useState(10)
  const [hopsTotalCount, setHopsTotalCount] = useState(0)
  const [hopsTotalPages, setHopsTotalPages] = useState(1)
  const [hopsSearchInput, setHopsSearchInput] = useState('')  // shown in input
  const [hopsSearchParam, setHopsSearchParam] = useState('')  // debounced → sent to API
  const hopsDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { data: userProfile } = useUserProfile()
  const { showLoader, hideLoader } = useLayout()

  const normalizeAccountUser = (user: AccountUser): AccountUser => {
    if (typeof user.status === 'string') {
      try {
        return {
          ...user,
          status: JSON.parse(user.status),
        }
      } catch {
        return user
      }
    }

    return user
  }

  const fetchAccountUsers = (type: 'TM' | 'OP') => {
    return axiosClientAuth
      .get(`/auth/users/?type=${type}`, {
        transformResponse: [(responseData) => responseData],
      })
      .then((resp) => {
        const parsed = parseJsonPreservingLargeIds<{ data?: { results?: AccountUser[] } }>(resp.data)
        return (parsed.data?.results || []).map(normalizeAccountUser)
      })
  }

  const fetchMyHops = (page = hopsPage, pageSize = hopsPageSize, search = hopsSearchParam) => {
    const params: Record<string, any> = { page, page_size: pageSize }
    if (search) params.search = search

    const queryString = new URLSearchParams(
      Object.entries(params).reduce((acc, [k, v]) => ({ ...acc, [k]: String(v) }), {})
    ).toString()

    axiosClientAuth
      .get(`/routes/hops/?${queryString}`, {
        transformResponse: [(responseData) => responseData],
      })
      .then((resp) => {
        const parsed = parseJsonPreservingLargeIds<{
          data?: {
            count?: number
            total_pages?: number
            results?: HopEntry[]
          }
        }>(resp.data)
        const responseData = parsed.data
        setData(responseData?.results || [])
        setHopsTotalCount(responseData?.count || 0)
        setHopsTotalPages(Math.max(1, responseData?.total_pages || 1))
        resetAll()
      })
      .catch((err) => {
        console.log(err)
        setData([])
        setHopsTotalCount(0)
        setHopsTotalPages(1)
      })
  }

  const getCurrentUserId = async () => {
    const res = await axiosClientAuth.get('/auth/profile/', {
      transformResponse: [(responseData) => responseData],
    })
    const parsed = parseJsonPreservingLargeIds<{ data?: { id?: string | number } }>(res.data)
    return parsed.data?.id || userProfile?.id
  }

  const fetchVendor = async () => {
    setVendorSearched(true)
    setVendorLoading(true)
    axiosClientAuth
      .get(`/auth/users/search/?q=${encodeURIComponent(vendorSearchTerm.trim())}`, {
        transformResponse: [(responseData) => responseData],
      })
      .then((res) => {
        const parsed = parseJsonPreservingLargeIds<{ data?: AccountUser }>(res.data)
        const telemarketer = parsed.data
        setVendorData(telemarketer ? [normalizeAccountUser(telemarketer)] : [])

      })
      .catch(() => setVendorData([]))
      .finally(() => {
        setVendorLoading(false)

      })
  }
  const fetchOperators = async () => {
    setOperatorLoading(true)
    fetchAccountUsers('OP')
      .then((users) => {
        setOperatorData(users)
      })
      .catch(() => setOperatorData([]))
      .finally(() => {
        setOperatorLoading(false)
      })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOperators()
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchMyHops(hopsPage, hopsPageSize, hopsSearchParam)
  }, [hopsPage, hopsPageSize, hopsSearchParam])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.SERIAL_NO' }),
        accessor: "srno",
        Cell: ({ ...props }) => {
          return <>{(hopsPage - 1) * hopsPageSize + props.row.index + 1}</>;
        },
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.NAME' }),
        accessor: (row: HopEntry) => row?.destination?.name,
        Cell: ({ ...props }) => (
          <>
            <span>{props.value || '--'}</span>
            {props.row.original?.destination?.id && (
              <span className='d-block text-muted fw-semibold fs-7'>
                {props.row.original.destination.id}
              </span>
            )}
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'HOPS.TABLE.COL_HOP_TYPE' }),
        accessor: (row: HopEntry) => row?.destination?.type,
        Cell: ({ ...props }) => {
          return <span className='fw-bold px-4 py-3'>{props.value}</span>
        },
      },
      {
        Header: intl.formatMessage({ id: 'HOPS.TABLE.COL_TM_OP_STATUS' }),
        Cell: ({ ...props }) => {
          const status = props.row?.original?.status?.destination?.active
          return status === null || status === undefined
            ? <span className='badge badge-light-warning fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.PENDING' })}</span>
            : status
              ? <span className='badge badge-light-success fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.APPROVED' })}</span>
              : <span className='badge badge-light-danger fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.REJECTED' })}</span>
        }
      },
      {
        Header: intl.formatMessage({ id: 'HOPS.TABLE.COL_SELF_DECLARATION' }),
        accessor: (row: HopEntry) => row.status?.self?.active,
        Cell: ({ ...props }) => {
          if (props.value === null || props.value === undefined) {
            return <span className='badge badge-light-warning fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.PENDING' })}</span>
          }
          return <>{props.value ? intl.formatMessage({ id: 'COMMON.STATUS.ENABLED' }) : intl.formatMessage({ id: 'COMMON.STATUS.DISABLED' })}</>;
        },
      },
      {
        id: 'action',
        Header: intl.formatMessage({ id: 'COMMON.TABLE.ACTION' }),
        Cell: ({ ...props }) => {
          return (
            <>
              <button
                className='btn h-35px btn-icon btn btn-light-primary me-3'
                onClick={() => changeHopStatus(props.row.original, true)}
                disabled={Boolean(props?.row?.original?.status?.self?.active)}
              >
                <KTIcon iconName='check' className='text-primary text-hover-white fs-1' />
              </button>
              <button
                className='btn h-35px btn-icon btn btn-light-danger me-3'
                onClick={() => changeHopStatus(props.row.original, false)}
                disabled={
                  props?.row?.original?.status?.self?.active != null &&
                  !Boolean(props?.row?.original?.status?.self?.active)
                }
              >
                <KTIcon iconName='cross' className='text-danger text-hover-white fs-1' />
              </button>
            </>
          );
        },
      },
    ],
    [hopsPage, hopsPageSize, intl]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns1 = useMemo<Column<AccountUser>[]>(
    () => [
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.SERIAL_NO' }),
        accessor: 'srno',
        Cell: ({ row }) => <>{row.index + 1}</>,
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.NAME' }),
        accessor: 'name',
        Cell: ({ value, row }: any) => (
          <>
            <span>{value || '--'}</span>
            {row.original?.id && (
              <span className='d-block text-muted fw-semibold fs-7'>
                {row.original.id}
              </span>
            )}
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.EMAIL' }),
        accessor: 'email',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.PHONE' }),
        accessor: 'phone',
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.STATUS' }),
        accessor: (row) => row.status?.self?.active,
        Cell: ({ value }) =>
          value ? (
            <span className='badge badge-light-success fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.ACTIVE' })}</span>
          ) : (
            <span className='badge badge-light-danger fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.INACTIVE' })}</span>
          ),
      },
      {
        id: 'action',
        Header: intl.formatMessage({ id: 'COMMON.TABLE.ACTION' }),
        Cell: ({ row }) => (
          <button
            className="btn h-35px w-150px btn-icon btn btn-light-primary me-3"
            onClick={() => onAddToHop(row.original)}
          >
            {intl.formatMessage({ id: 'HOPS.BUTTON.ADD_TO_HOPS' })}
          </button>
        ),
      },
    ],
    [intl]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns2 = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.SERIAL_NO' }),
        accessor: "srno",
        Cell: ({ ...props }) => {
          return <>{props.row.index + 1}</>;
        },
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.EMAIL' }),
        accessor: 'email',
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.PHONE' }),
        accessor: 'phone',
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.STATUS' }),
        accessor: (row: AccountUser) => row.status?.self?.active,
        Cell: ({ value }) =>
          value ? (
            <span className='badge badge-light-success fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.ACTIVE' })}</span>
          ) : (
            <span className='badge badge-light-danger fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.INACTIVE' })}</span>
          ),
      },
      {
        id: 'action',
        Header: intl.formatMessage({ id: 'COMMON.TABLE.ACTION' }),
        Cell: ({ ...props }) => {
          return (
            <>
              <button
                className="btn h-35px w-150px btn-icon btn btn-light-primary me-3"
                onClick={() => onAddToHop(props.row.original)}
              >
                {intl.formatMessage({ id: 'HOPS.BUTTON.ADD_TO_HOPS' })}
              </button>
            </>
          );
        },
      },
    ],
    [intl]
  );

  const mTable = useTable<any>(
    {
      columns: columns as Column<any>[],
      data: data as any[],
    },
    useSortBy
  );

  const mNodeTable1 = useTable<any>(
    {
      columns: columns1 as Column<any>[],
      data: vendorData as any[],
      initialState: { pageSize: 10 },
      autoResetPage: true,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const mNodeTable2 = useTable<any>(
    {
      columns: columns2 as Column<any>[],
      data: selectedOperator as any[],
      initialState: { pageSize: 10 },
      autoResetPage: true,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const resetAll = () => {
    setVendorData([])
    setSelectedOperator([])
    setVendorSearchTerm('')
    setVendorSearched(false)
  }

  const onAddToHop = (data: any) => {
    showLoader()
    getCurrentUserId()
      .then((source) => {
        const destination = data?.id || data?.uuid

        if (!source || !destination) {
          throw new Error('Unable to identify source or destination hop')
        }

        return axiosClientAuth.post(
          '/routes/hops/create/',
          `{"source":${formatJsonId(source)},"destination":${formatJsonId(destination)}}`
        )
      })
      .then((resp) => {
        fetchMyHops(hopsPage, hopsPageSize, hopsSearchParam);
        fetchOperators()
        resetAll()
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.SUCCESS' }),
          text: intl.formatMessage({ id: 'HOPS.MESSAGES.HOP_ADDED' }),
          icon: 'success',
        })
      })
      .catch((err) => {
        console.log(err)
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.FAILED' }),
          text: err?.response?.data?.message || intl.formatMessage({ id: 'HOPS.MESSAGES.HOP_ADD_FAILED' }),
          icon: 'error',
        })
      })
      .finally(() => {
        hideLoader()
      })
  }

  const changeHopStatus = (data: HopEntry, status: boolean) => {
    showLoader()
    axiosClientAuth
      .patch(`/routes/hops/${data?.id}/status/`, { active: status })
      .then((resp) => {
        fetchMyHops(hopsPage, hopsPageSize, hopsSearchParam);
        fetchOperators()
        resetAll()
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.SUCCESS' }),
          text: status
            ? intl.formatMessage({ id: 'HOPS.MESSAGES.HOP_ENABLED' })
            : intl.formatMessage({ id: 'HOPS.MESSAGES.HOP_DISABLED' }),
          icon: 'success',
        })
      })
      .catch((err) => {
        console.log(err)
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.FAILED' }),
          text: err?.response?.data?.message || intl.formatMessage({ id: 'HOPS.MESSAGES.HOP_UPDATE_FAILED' }),
          icon: 'error',
        })
      })
      .finally(() => {
        hideLoader()
      })
  }

  const deleteHop = (data: HopEntry) => {
    showLoader()
    axiosClientAuth
      .delete(`/routes/hops/${data?.id}/`)
      .then((resp) => {
        fetchMyHops(hopsPage, hopsPageSize, hopsSearchParam);
        fetchOperators()
        resetAll()
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.SUCCESS' }),
          text: intl.formatMessage({ id: 'HOPS.MESSAGES.HOP_DELETED' }),
          icon: 'success',
        })
      })
      .catch((err) => {
        console.log(err)
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.FAILED' }),
          text: err?.response?.data?.message || intl.formatMessage({ id: 'HOPS.MESSAGES.HOP_DELETE_FAILED' }),
          icon: 'error',
        })
      })
      .finally(() => {
        hideLoader()
      })
  }

  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selected = operatorData.find((operator) => String(operator.id) === selectedId)

    setSelectedOperator(selected ? [selected] : []);
  };
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.MY_HOPS' })}</PageTitle>
      <div className="row g-5 g-xl-8">
        <div className="col-12">
          <div className={`card`}>
            <div className="card-header border-0 pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold fs-3 mb-1">
                  {intl.formatMessage({ id: 'HOPS.TABLE.TITLE' })}
                </span>
                <span className="text-muted fw-semibold fs-7">
                  {intl.formatMessage({ id: 'HOPS.TABLE.TOTAL_RECORDS' }, { count: hopsTotalCount })}
                </span>
              </h3>
              <div className="d-flex align-items-center">
                <div className="d-flex align-items-center position-relative my-1 ms-2">
                  <KTIcon iconName="magnifier" className="text-white-500 position-absolute ms-2" />
                  <input
                    type="text"
                    id="kt_filter_search"
                    className="form-control form-control-solid form-select-sm w-150px ps-9"
                    placeholder={intl.formatMessage({ id: 'HOPS.TABLE.SEARCH_PLACEHOLDER' })}
                    value={hopsSearchInput}
                    onChange={(e) => {
                      const val = e.target.value
                      setHopsSearchInput(val)
                      if (hopsDebounceTimer.current) clearTimeout(hopsDebounceTimer.current)
                      hopsDebounceTimer.current = setTimeout(() => {
                        setHopsSearchParam(val)
                        setHopsPage(1)
                      }, 400)
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="card-body pt-3">
              <div className="table-responsive">
                <table
                  {...mTable.getTableProps()}
                  className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer"
                >
                  <thead>
                    {mTable.headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            className={column.id === 'action' ? "w-200px" : ""}
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                          >
                            <span className="text-gray-400 fs-7">{column.render("Header")}</span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <KTIcon iconName="down" className="text-gray ms-1" />
                              ) : (
                                <KTIcon iconName="up" className="text-gray ms-1" />
                              )
                            ) : (
                              ""
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...mTable.getTableBodyProps()}>
                    {(mTable.rows.length > 0 &&
                      mTable.rows.map((row) => {
                        mTable.prepareRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td className="fw-bold" {...cell.getCellProps()}>
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })) || (
                        <tr>
                          <td colSpan={7} className="text-center">
                            <span className="fw-bold">
                              {intl.formatMessage({ id: 'COMMON.MESSAGES.NO_DATA_FOUND' })}
                            </span>
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
                <div className="d-flex justify-content-between align-items-center mt-5">
                  <div>
                    <select
                      className="form-select form-select-sm form-select-solid"
                      value={hopsPageSize}
                      onChange={(e) => {
                        setHopsPageSize(parseInt(e.target.value, 10))
                        setHopsPage(1)
                      }}
                    >
                      {[5, 10, 20, 25, 50, 100].map((item) => (
                        <option>{item}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span>
                      {intl.formatMessage({ id: 'COMMON.PAGINATION.PAGE' })}{' '}
                      <strong>
                        {hopsPage} {intl.formatMessage({ id: 'COMMON.PAGINATION.OF' })} {hopsTotalPages}
                      </strong>
                    </span>
                  </div>
                  <div>
                    <button
                      className="btn btn-sm"
                      onClick={() => setHopsPage((prev) => Math.max(1, prev - 1))}
                      disabled={hopsPage <= 1}
                    >
                      <KTIcon iconName="left" className="text-gray fs-1 me-5" />
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => setHopsPage((prev) => Math.min(hopsTotalPages, prev + 1))}
                      disabled={hopsPage >= hopsTotalPages}
                    >
                      <KTIcon iconName="right" className="text-gray fs-1 me-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-5 g-xl-8 mt-5'>
        <div className='col-12'>
          <div className={`card`}>
            <div className="card-px text-center pt-15 pb-15">
              <h2 className="fs-2x fw-bold mb-0">{intl.formatMessage({ id: 'HOPS.SEARCH_TM.TITLE' })}</h2>
              <p className="text-gray-500 fs-4 fw-semibold py-3">
                {intl.formatMessage({ id: 'HOPS.SEARCH_TM.SUBTITLE' })}
              </p>

              <div className="card-body p-0">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="position-relative w-md-400px me-md-2 d-flex align-items-center ">
                    <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2 fs-2' />
                    <input
                      type="text"
                      className="form-control form-control-solid ps-10 p-3.5"
                      name="search" value={vendorSearchTerm}
                      onChange={(e) => {
                        setVendorSearchTerm(e.target.value)
                        setVendorSearched(false)
                      }}
                      placeholder={intl.formatMessage({ id: 'HOPS.SEARCH_TM.PLACEHOLDER' })}
                    />
                  </div>
                  <div className="d-flex align-items-center">
                    <button type="submit" className="btn btn-primary me-2" onClick={fetchVendor}>
                      {intl.formatMessage({ id: 'COMMON.BUTTON.SEARCH' })}
                    </button>
                    <button type="button" className="btn btn-light" onClick={() => {
                      setVendorSearchTerm('');
                      setVendorData([]);
                      setVendorSearched(false)
                    }}>
                      {intl.formatMessage({ id: 'COMMON.BUTTON.CLEAR' })}
                    </button>
                  </div>
                </div>
              </div>

              {vendorLoading ?
                <div className="d-flex justify-content-center align-items-center py-10">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{intl.formatMessage({ id: 'COMMON.MESSAGES.LOADING' })}</span>
                  </div>
                </div>
                :
                vendorData.length > 0 ?
                  <>
                    <div className="separator separator-dashed mt-9 mb-6"></div>

                    <div className='table-responsive text-start'>
                      <table
                        {...mNodeTable1.getTableProps()}
                        className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                      >
                        <thead>
                          {mNodeTable1.headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                              {headerGroup.headers.map((column) => (
                                <th
                                  className={column.id === 'action' ? 'w-200px' : ''}
                                  {...column.getHeaderProps(column.getSortByToggleProps())}
                                >
                                  <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                                  {column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <KTIcon iconName='down' className='text-gray ms-1' />
                                    ) : (
                                      <KTIcon iconName='up' className='text-gray ms-1' />
                                    )
                                  ) : (
                                    ''
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody {...mNodeTable1.getTableBodyProps()}>
                          {(mNodeTable1.page.length > 0 &&
                            mNodeTable1.page.map((row) => {
                              mNodeTable1.prepareRow(row)
                              return (
                                <tr {...row.getRowProps()}>
                                  {row.cells.map((cell) => (
                                    <td className='fw-bold' {...cell.getCellProps()}>
                                      {cell.render('Cell')}
                                    </td>
                                  ))}
                                </tr>
                              )
                            })) || (
                              <tr>
                                <td colSpan={8} className='text-center'>
                                  <span className='fw-bold'>
                                    {intl.formatMessage({ id: 'COMMON.MESSAGES.NO_DATA_FOUND' })}
                                  </span>
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>
                      <div className='d-flex justify-content-between align-items-center mt-5'>
                        <div>
                          <select
                            className='form-select form-select-sm form-select-solid'
                            value={mNodeTable1.state.pageSize}
                            onChange={(e) => mNodeTable1.setPageSize(parseInt(e.target.value))}
                          >
                            {[10, 25, 50, 100].map((item) => (
                              <option>{item}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <span>
                            {intl.formatMessage({ id: 'COMMON.PAGINATION.PAGE' })}{' '}
                            <strong>
                              {mNodeTable1.state.pageIndex + 1} {intl.formatMessage({ id: 'COMMON.PAGINATION.OF' })} {mNodeTable1.pageOptions.length}
                            </strong>
                          </span>
                        </div>
                        <div>
                          <button
                            className='btn btn-sm'
                            onClick={() => mNodeTable1.previousPage()}
                            disabled={!mNodeTable1.canPreviousPage}
                          >
                            <KTIcon iconName='left' className='text-gray fs-1 me-5' />
                          </button>
                          <button
                            className='btn btn-sm'
                            onClick={() => mNodeTable1.nextPage()}
                            disabled={!mNodeTable1.canNextPage}
                          >
                            <KTIcon iconName='right' className='text-gray fs-1 me-5' />
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                  : vendorSearched && vendorData.length === 0 &&
                  <div className="mt-5">
                    <span className="fw-bold text-danger">
                      {intl.formatMessage({ id: 'HOPS.SEARCH_TM.NO_RESULTS' })}
                    </span>
                  </div>
              }
            </div>
          </div>
        </div>
      </div>

      <div className='row g-5 g-xl-8 mt-5'>
        <div className='col-12'>
          <div className='card'>
            <div className="card-px text-center pt-15 pb-15">
              <h2 className="fs-2x fw-bold mb-0">{intl.formatMessage({ id: 'HOPS.SELECT_OP.TITLE' })}</h2>
              <p className="text-gray-500 fs-4 fw-semibold py-3">
                {intl.formatMessage({ id: 'HOPS.SELECT_OP.SUBTITLE' })}
              </p>

              <div className="card-body p-0">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="position-relative w-md-400px me-md-2 d-flex align-items-center">
                    <KTIcon iconName='arrow-down' className='text-white-500 position-absolute ms-2 fs-2' />
                    <select
                      className="form-control form-control-solid ps-10 p-3.5"
                      value={selectedOperator.length > 0 ? String(selectedOperator[0].id) : ''}
                      onChange={handleOperatorChange}
                      disabled={operatorLoading}
                    >
                      <option value="" disabled>
                        {intl.formatMessage({ id: 'HOPS.SELECT_OP.PLACEHOLDER' })}
                      </option>
                      {operatorData.map(operator => (
                        <option key={`${operator.id}-${operator.email}`} value={operator.id}>
                          {operator.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-light p-3.5"
                      onClick={() => {
                        setSelectedOperator([]);
                      }}
                    >
                      {intl.formatMessage({ id: 'COMMON.BUTTON.CLEAR' })}
                    </button>
                  </div>
                </div>
              </div>

              {operatorLoading ? (
                <div className="d-flex justify-content-center align-items-center py-10">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{intl.formatMessage({ id: 'COMMON.MESSAGES.LOADING' })}</span>
                  </div>
                </div>
              ) : (
                selectedOperator.length > 0 && (
                  <>
                    <div className="separator separator-dashed mt-9 mb-6"></div>

                    <div className='table-responsive text-start'>
                      <table
                        {...mNodeTable2.getTableProps()}
                        className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                      >
                        <thead>
                          {mNodeTable2.headerGroups.map((headerGroup, i) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                              {headerGroup.headers.map((column, idx) => (
                                <th
                                  className={column.id === 'action' ? 'w-200px' : ''}
                                  {...column.getHeaderProps(column.getSortByToggleProps())}
                                  key={idx}
                                >
                                  <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                                  {column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <KTIcon iconName='down' className='text-gray ms-1' />
                                    ) : (
                                      <KTIcon iconName='up' className='text-gray ms-1' />
                                    )
                                  ) : (
                                    ''
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody {...mNodeTable2.getTableBodyProps()}>
                          {(mNodeTable2.page.length > 0 &&
                            mNodeTable2.page.map((row, i) => {
                              mNodeTable2.prepareRow(row);
                              return (
                                <tr {...row.getRowProps()} key={i}>
                                  {row.cells.map((cell, i) => (
                                    <td className='fw-bold' {...cell.getCellProps()} key={i}>
                                      {cell.render('Cell')}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })) || (
                              <tr>
                                <td colSpan={8} className='text-center'>
                                  <span className='fw-bold'>
                                    {intl.formatMessage({ id: 'COMMON.MESSAGES.NO_DATA_FOUND' })}
                                  </span>
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>

                      <div className='d-flex justify-content-between align-items-center mt-5'>
                        <div>
                          <select
                            className='form-select form-select-sm form-select-solid'
                            value={mNodeTable2.state.pageSize}
                            onChange={(e) => mNodeTable2.setPageSize(parseInt(e.target.value))}
                          >
                            {[10, 25, 50, 100].map((item) => (
                              <option key={item}>{item}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <span>
                            {intl.formatMessage({ id: 'COMMON.PAGINATION.PAGE' })}{' '}
                            <strong>
                              {mNodeTable2.state.pageIndex + 1} {intl.formatMessage({ id: 'COMMON.PAGINATION.OF' })} {mNodeTable2.pageOptions.length}
                            </strong>
                          </span>
                        </div>
                        <div>
                          <button
                            className='btn btn-sm'
                            onClick={() => mNodeTable2.previousPage()}
                            disabled={!mNodeTable2.canPreviousPage}
                          >
                            <KTIcon iconName='left' className='text-gray fs-1 me-5' />
                          </button>
                          <button
                            className='btn btn-sm'
                            onClick={() => mNodeTable2.nextPage()}
                            disabled={!mNodeTable2.canNextPage}
                          >
                            <KTIcon iconName='right' className='text-gray fs-1 me-5' />
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
